import React, { useState } from "react";
import { Copy, Check, Code } from "lucide-react";
import { toast } from "sonner";

/**
 * Self-contained "Embed this calculator" block for /check-a-deduction.
 *
 * Renders a pre-built iframe snippet that third-party sites can paste
 * directly onto their pages. The embed itself ships with auto-resize
 * via window.postMessage, so the snippet listens for that and bumps
 * its own height to match the calculator's rendered content. Hosts
 * who skip the listener still get a working iframe (just at the
 * default 920px height).
 *
 * Keeping this in its own file so the landing page stays readable.
 */
export default function EmbedSnippetBlock() {
  const [copied, setCopied] = useState(false);

  const snippet = `<iframe
  src="https://tenanttrail.net/embed/check-a-deduction"
  title="Is this bond deduction fair? - free calculator by TenantTrail"
  width="100%"
  height="920"
  style="border:0;max-width:680px;display:block;margin:0 auto;"
  loading="lazy"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>
<script>
  // Optional: auto-resize the iframe to match the calculator's content
  window.addEventListener("message", function (e) {
    if (!e.data || e.data.type !== "tenanttrail:embed:resize") return;
    var f = document.querySelector('iframe[src*="tenanttrail.net/embed/check-a-deduction"]');
    if (f && e.data.height) f.style.height = e.data.height + "px";
  });
</script>`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed snippet copied. Paste it into your page HTML.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy. Select the code manually.");
    }
  };

  return (
    <section className="mb-12" data-testid="embed-snippet-block">
      <div className="bg-white border border-[#E5E0D6] p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <Code size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
          <h2 className="font-serif text-2xl text-[#0D2B52]">
            Embed this calculator on your site
          </h2>
        </div>
        <p className="text-[14.5px] text-[#6B7280] leading-relaxed mb-4">
          Run a renter blog, a tenancy support page or a community newsletter? Paste this snippet into your page HTML and the calculator drops in. It is free, no key needed, no tracking pixels. Every embed carries the TenantTrail watermark and a small attribution link, that is all we ask.
        </p>

        <div className="relative">
          <pre
            className="bg-[#0D2B52] text-[#E6EEF8] font-mono text-[12px] leading-relaxed p-4 overflow-x-auto whitespace-pre rounded-none"
            data-testid="embed-snippet-pre"
          >
            <code>{snippet}</code>
          </pre>
          <button
            type="button"
            onClick={onCopy}
            className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[11px] uppercase tracking-[0.1em] font-mono px-3 py-1.5 border border-white/20 transition-all"
            data-testid="embed-snippet-copy"
          >
            {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.75} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <ul className="text-[12.5px] text-[#6B7280] mt-5 space-y-1.5 leading-relaxed">
          <li>
            <strong className="text-[#1A1A1A]">Responsive</strong>. Set a max width that suits your layout, the iframe scales down on mobile.
          </li>
          <li>
            <strong className="text-[#1A1A1A]">Auto-resizing</strong>. The optional script bumps the iframe height to match the calculator content as the user fills it in.
          </li>
          <li>
            <strong className="text-[#1A1A1A]">No personal details collected</strong>. The calculator runs entirely in the visitor&apos;s browser. The share card and watermark are part of the embed.
          </li>
          <li>
            <strong className="text-[#1A1A1A]">Want a custom look?</strong> Drop us a line, we are happy to tune the colours for partner sites.
          </li>
        </ul>
      </div>
    </section>
  );
}
